import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, IdCard, Pencil, Plus, Trash2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { EmptyState } from "../../../../shared/ui/primitives";
import {
    customFieldRefusal,
    MAX_CUSTOM_FIELDS,
    moveField,
    useCenterPatientFields,
    useDeletePatientField,
    useReorderPatientFields,
    type PatientCustomField,
} from "../../../center";
import PatientFieldFormModal from "./PatientFieldFormModal";

const BADGE = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium";
const ICON_BUTTON =
    "inline-flex size-9 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken disabled:opacity-40 disabled:hover:bg-transparent";

/**
 * The centre's own fields for its patients — a student number, a file
 * reference, a funding programme — in the order they are shown and filled.
 * Their values are edited on each patient; here only what the fields are.
 * Shown under the centre's patient list, where the values are filled.
 */
export default function CenterPatientFieldsSection({ centerId }: { centerId: string }) {
    const { t } = useTranslation();
    const { fields, loading: fieldsLoading, failed, retry } = useCenterPatientFields(centerId);
    const reorder = useReorderPatientFields(centerId);
    const remove = useDeletePatientField(centerId);

    const [error, setError] = useState<string | null>(null);
    // The field being edited; `null` is a new one, `undefined` no dialog.
    const [editing, setEditing] = useState<PatientCustomField | null | undefined>(undefined);
    const [deleting, setDeleting] = useState<PatientCustomField | null>(null);

    const atLimit = fields.length >= MAX_CUSTOM_FIELDS;

    const move = async (index: number, by: -1 | 1) => {
        setError(null);
        try {
            await reorder.mutateAsync(moveField(fields, index, by));
        } catch (err) {
            setError(customFieldRefusal(err).message);
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        setError(null);
        try {
            await remove.mutateAsync(deleting.id);
        } catch (err) {
            setError(customFieldRefusal(err).message);
        }
        setDeleting(null);
    };

    const newButton = (
        <Button onClick={() => setEditing(null)} disabled={atLimit} startIcon={<Plus size={16} aria-hidden="true" />}>
            {t("center.patientFields.newTitle")}
        </Button>
    );

    return (
        <>
            <section
                id="patient-fields"
                aria-labelledby="patient-fields-title"
                className="mt-6 rounded-2xl border border-border-subtle bg-surface-raised p-6"
            >
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 id="patient-fields-title" className="mb-1 text-lg font-semibold text-content-primary">
                            {t("center.patientFields.title")}
                        </h2>
                        <p className="text-sm text-content-secondary">{t("center.patientFields.subtitle")}</p>
                    </div>
                    {fields.length > 0 && (
                        <div className="flex flex-col items-end gap-1">
                            {newButton}
                            {atLimit && (
                                <p className="text-xs text-content-tertiary">
                                    {t("center.patientFields.limitHint", { max: MAX_CUSTOM_FIELDS })}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {error && (
                    <p role="alert" className="mb-4 text-sm text-status-critical">
                        {t(error)}
                    </p>
                )}

                {fieldsLoading ? (
                    <p role="status" className="text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                ) : failed ? (
                    // Not "no fields yet": a failed load says nothing about whether there are any.
                    <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-status-critical">{t("center.patientFields.errorLoading")}</span>
                        <Button size="sm" variant="outline" onClick={retry}>
                            {t("center.patientFields.card.retry")}
                        </Button>
                    </div>
                ) : fields.length === 0 ? (
                    <EmptyState
                        icon={<IdCard className="h-6 w-6" />}
                        title={t("center.patientFields.emptyTitle")}
                        description={t("center.patientFields.empty")}
                        action={newButton}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-border-subtle text-xs uppercase tracking-wide text-content-tertiary">
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patientFields.label")}
                                    </th>
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patientFields.type")}
                                    </th>
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patientFields.rules")}
                                    </th>
                                    <th scope="col" className="py-2 font-medium">
                                        <span className="sr-only">{t("center.team.actions")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((field, index) => (
                                    <tr key={field.id} className="border-b border-border-subtle last:border-0">
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-content-primary">{field.label}</p>
                                            {field.field_type === "select" && field.options.length > 0 && (
                                                <p className="text-xs text-content-tertiary">{field.options.join(", ")}</p>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className={`${BADGE} bg-surface-sunken text-content-secondary`}>
                                                {t(`center.patientFields.types.${field.field_type}`)}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className="inline-flex flex-wrap gap-1.5">
                                                {field.required && (
                                                    <span className={`${BADGE} bg-accent-tint text-accent-on-tint`}>
                                                        {t("center.patientFields.required")}
                                                    </span>
                                                )}
                                                {field.unique && (
                                                    <span className={`${BADGE} bg-accent-tint text-accent-on-tint`}>
                                                        {t("center.patientFields.unique")}
                                                    </span>
                                                )}
                                                {!field.required && !field.unique && (
                                                    <span className="text-content-tertiary">—</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="py-3 text-right">
                                            <span className="inline-flex gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => void move(index, -1)}
                                                    disabled={index === 0 || reorder.isPending}
                                                    aria-label={t("center.patientFields.moveUp", { name: field.label })}
                                                    className={ICON_BUTTON}
                                                >
                                                    <ArrowUp size={15} aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void move(index, 1)}
                                                    disabled={index === fields.length - 1 || reorder.isPending}
                                                    aria-label={t("center.patientFields.moveDown", { name: field.label })}
                                                    className={ICON_BUTTON}
                                                >
                                                    <ArrowDown size={15} aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditing(field)}
                                                    aria-label={t("center.patientFields.edit", { name: field.label })}
                                                    className={ICON_BUTTON}
                                                >
                                                    <Pencil size={15} aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDeleting(field)}
                                                    aria-label={t("center.patientFields.delete", { name: field.label })}
                                                    className="inline-flex size-9 items-center justify-center rounded-lg text-content-secondary hover:bg-status-critical-tint hover:text-status-critical"
                                                >
                                                    <Trash2 size={15} aria-hidden="true" />
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <PatientFieldFormModal
                isOpen={editing !== undefined}
                onClose={() => setEditing(undefined)}
                centerId={centerId}
                field={editing}
            />
            <ConfirmDialog
                isOpen={deleting !== null}
                destructive
                title={t("center.patientFields.deleteTitle")}
                message={t("center.patientFields.deleteConfirm", { name: deleting?.label ?? "" })}
                confirmLabel={t("common.delete")}
                isBusy={remove.isPending}
                onConfirm={() => void confirmDelete()}
                onCancel={() => setDeleting(null)}
            />
        </>
    );
}
